import { useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';

/*
 * Where the page sits after a navigation. One component decides it, for all
 * four surfaces.
 *
 * Without this, clicking a header link while scrolled halfway down leaves the
 * *new* page scrolled halfway down. The browser only resets scroll on a real
 * document load, and a client-side route change is a `pushState` — so nothing
 * moves. **All three portals have the same bug for the same reason**: every
 * surface scrolls the window (`min-h-screen` on the outer element and a
 * `<main>` in normal flow, in each of the four layouts), so this is mounted
 * once at the router rather than copied into each one.
 *
 * **react-router's own `<ScrollRestoration />` is not available here.** It is
 * built on `useMatches()` and `useNavigation()`, which exist only under a data
 * router. App.tsx uses `<BrowserRouter>` + `<Routes>`, so the choice is these
 * forty lines or a router rewrite.
 *
 * Three cases. The two that are *not* "go to the top" are the ones worth
 * reading.
 */
export function ScrollOnNavigate() {
  /*
   * `key` is in here for one reason, and it is the site's main conversion
   * button. The header's "Get a Free Quote" is sticky and stays on screen while
   * somebody reads /contact — so it gets clicked a second time, from further
   * down, with `#quote` already in the address bar. Path and hash are both
   * unchanged, so an effect watching only those would not fire and the button
   * would silently do nothing. `key` is new on every navigation, including a
   * repeat of the current one.
   *
   * The thing to know before adding a screen: this therefore also fires on a
   * search-param change. Nothing does that today — the only `useSearchParams`
   * in the app reads a password-reset token — but a list page that put its
   * filters in the URL would jump to the top on every filter, and would want a
   * way to opt out.
   */
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  /*
   * The previous path, so that moving *within* a page can be told from arriving
   * at one, and the first render from everything after it. `null` until the
   * effect has run once.
   */
  const previousPathname = useRef<string | null>(null);

  useEffect(() => {
    const previous = previousPathname.current;
    previousPathname.current = pathname;

    const firstRender = previous === null;
    const arrived = previous !== pathname;

    /*
     * **Back, forward and reload all arrive as POP, and all three want the
     * scroll position left alone.** So does the very first render — POP is
     * react-router's action for a history object nobody has navigated yet.
     *
     * `history.scrollRestoration` is `auto` — nothing in this app sets it to
     * `manual`, and react-router only does that inside `<ScrollRestoration />`,
     * which is not mounted — so the browser is already responsible for putting
     * the page back where it was. Scrolling here would fight it and win, and
     * the back button would lose the reader's place every time.
     *
     * The one exception is a page **opened** at an anchor. The browser did try
     * that fragment, on the real document load, and found nothing: React had
     * not rendered yet. There is no restored position to fight, and without
     * this a link somebody shares to /contact#quote lands at the top of the
     * page.
     *
     * > Worth knowing before debugging it: on a URL Chrome *does* hold a saved
     * > offset for, its own load-time restoration runs after this and wins, so
     * > a revisit lands where it left off rather than on the anchor. Measured,
     * > not assumed. That is the browser doing the job this defers to
     * > everywhere else, so it is left alone.
     */
    const openedAtAnAnchor = firstRender && hash !== '';
    if (navigationType === NavigationType.Pop && !openedAtAnAnchor) return;

    if (hash) {
      /*
       * **A hash link does nothing on its own**, which is what building this
       * turned up rather than something it set out to fix. The header's "Get a
       * Free Quote" and both home-page CTAs point at `/contact#quote`, and a
       * `pushState` is not a fragment navigation: the browser never scrolls,
       * and the element would not exist yet if it looked. All three put
       * `#quote` in the address bar and left the visitor at the top of the
       * page, six hundred pixels above the form they had just asked for.
       *
       * `decodeURIComponent`, because the fragment in a URL is percent-encoded
       * and an id need not be ASCII.
       */
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));

      if (target) {
        /*
         * **Glide within a page; jump between them.**
         *
         * `auto` defers to `scroll-behavior` in styles/index.css, which is
         * smooth — and which sits inside a `prefers-reduced-motion` query, so
         * anybody who has asked for less movement gets the jump without this
         * having to ask a second time.
         *
         * Arriving is a different thing. The new page mounts at the *old*
         * page's scroll offset, so a smooth scroll would slide through a page
         * the visitor has never seen on the way to the form. They asked for the
         * form; put them on it.
         *
         * Where it lands is the target's own `scroll-mt-*`, which is what keeps
         * the sticky header off it.
         */
        target.scrollIntoView({ behavior: arrived ? 'instant' : 'auto' });
        return;
      }

      // An anchor that is not on this page — a stale link, or one pointing at
      // something conditionally rendered. The top is a better answer than
      // wherever the previous page happened to be left.
    }

    /*
     * **`instant`, and that is not a detail.** `scroll-behavior: smooth` is
     * global, so a plain `window.scrollTo(0, 0)` *animates*: the full height of
     * the page being left, scenery included, on the way to a page already
     * asked for. Smooth is for moving within a page. A navigation should be
     * over by the time it is drawn.
     */
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, key, navigationType]);

  return null;
}
